import React, { useState, useEffect } from 'react';
import { SubscriptionGate } from './SubscriptionGate';
import { useFirebase } from '../contexts/FirebaseContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { getTenant, updateTenant } from '../services/tenantService';
import { getSettings } from '../services/settingsService';
import { parseTransactionDate } from '../lib/utils';
import { isAllocatedOrPendingPOS } from '../lib/transactionUtils';
import { formatCurrency } from '../lib/formatters';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ComposedChart
} from 'recharts';
import {
  TrendingUp,
  Award,
  Users,
  Flame,
  Info,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  ArrowUpRight,
  Compass,
  RefreshCw
} from 'lucide-react';

export const BenchmarkTools: React.FC = () => {
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [tenantData, setTenantData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [minSampleSize, setMinSampleSize] = useState<number>(10);
  const [averageTips, setAverageTips] = useState<number>(0);
  const [rank, setRank] = useState<number>(0);
  const [totalPeers, setTotalPeers] = useState<number>(0);
  const [isInsufficient, setIsInsufficient] = useState<boolean>(true);
  const [dateRange, setDateRange] = useState<'last_30_days' | 'month_to_date' | 'today' | 'all_time'>('last_30_days');
  const [myTotalTipsCalculated, setMyTotalTipsCalculated] = useState<number>(0);
  const { tenantId, user } = useFirebase();
  
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@growezy.ai';

  const fetchBenchmarkAndTenantData = async (showNotification = false) => {
    if (!tenantId || tenantId === 'undefined' || tenantId === 'null') return;
    setLoading(true);
    try {
      if (showNotification) {
        setStatus({ type: 'info', message: 'Re-indexing local business registers...' });
      }

      // Fast self-upgrade/activation for local testing superadmin to bypass plan limits
      if (isSuperAdmin) {
        await updateTenant(tenantId, { planId: 'enterprise', subscriptionStatus: 'active' });
      }

      // 1. Fetch current tenant details to get real dbaName, and businessType
      const tData = await getTenant(tenantId);
      let bType = 'General';
      if (tData) {
        setTenantData(tData);
        bType = tData.businessType || 'General';
      }

      // 2. Fetch custom benchmark sample size requirement
      let currentMinSampleSize = 10;
      const settingsRef = doc(db, 'platform_config', 'benchmark_settings');
      const settingsSnap = await getDoc(settingsRef);
      if (settingsSnap.exists()) {
        const sData = settingsSnap.data();
        if (typeof sData.minSampleSize === 'number') {
          currentMinSampleSize = sData.minSampleSize;
          setMinSampleSize(sData.minSampleSize);
        }
      }

      // 3. Dynamic transactional compilation
      const settingsData = await getSettings(tenantId);
      const timeZone = settingsData?.timezone || 'UTC';

      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const [year, month, day] = formatter.format(new Date()).split('-').map(Number);
      
      const dEnd = new Date(year, month - 1, day);
      const dStart = new Date(year, month - 1, day);

      let startDay = '';
      let endDay = '';

      switch (dateRange) {
        case 'today':
          break;
        case 'last_30_days':
          dStart.setDate(dStart.getDate() - 30);
          break;
        case 'month_to_date':
          dStart.setDate(1);
          break;
        default:
          dStart.setDate(dStart.getDate() - 30);
      }
      startDay = dStart.getFullYear() + '-' + String(dStart.getMonth() + 1).padStart(2, '0') + '-' + String(dStart.getDate()).padStart(2, '0');
      endDay = dEnd.getFullYear() + '-' + String(dEnd.getMonth() + 1).padStart(2, '0') + '-' + String(dEnd.getDate()).padStart(2, '0');

      // Query subcollection master_transactions to find real dynamic totals matching the selected dateRange
      const masterRef = collection(db, 'tenants', tenantId, 'master_transactions');
      const masterSnap = await getDocs(masterRef);
      const allTxs = masterSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));

      const filteredTxs = allTxs.filter(t => {
        if (dateRange === 'all_time') return true;
        const normalizedDate = parseTransactionDate(t.tipDate || t.timestamp);
        return normalizedDate >= startDay && normalizedDate <= endDay;
      });

      // Verify allocated or pending POS tips in date range using standard helper
      const allocatedTxs = filteredTxs.filter(t => isAllocatedOrPendingPOS(t, allTxs));
      const dynamicTipsTotal = allocatedTxs.reduce((sum, t) => sum + (t.amount || 0), 0);
      const dynamicTipsRounded = Number(dynamicTipsTotal.toFixed(2));
      
      setMyTotalTipsCalculated(dynamicTipsRounded);

      // 4. Query firestore dynamically to compile peer benchmarks
      const tenantsRef = collection(db, 'tenants');
      const q = query(tenantsRef, where('businessType', '==', bType));
      const qSnap = await getDocs(q);
      const peers = qSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));

      // Overwrite or dynamically inject the local tenant data into peer comparisons
      const peerIdx = peers.findIndex(p => p.id === tenantId);
      if (peerIdx !== -1) {
        peers[peerIdx] = { ...peers[peerIdx], totalTips: dynamicTipsRounded };
      } else if (tData) {
        peers.push({ id: tenantId, ...tData, totalTips: dynamicTipsRounded });
      }

      // Sort descending by totalTips to calculate rank
      peers.sort((a, b) => (b.totalTips || 0) - (a.totalTips || 0));

      const count = peers.length;
      const sumTips = peers.reduce((sum, p) => sum + (p.totalTips || 0), 0);
      const avg = count > 0 ? Number((sumTips / count).toFixed(2)) : 0;
      
      // Find our indexes in the sorted peers list
      const myIndex = peers.findIndex(p => p.id === tenantId);
      const calculatedRank = myIndex !== -1 ? myIndex + 1 : 1;
      const calcInsufficient = count < currentMinSampleSize;

      setTotalPeers(count);
      setAverageTips(avg);
      setRank(calculatedRank);
      setIsInsufficient(calcInsufficient);

      if (showNotification) {
        setStatus({ type: 'success', message: 'Successfully re-indexed and synchronized with live registry!' });
        setTimeout(() => setStatus(null), 4000);
      }
    } catch (error: any) {
      console.error('Error fetching dynamic benchmarks:', error);
      if (showNotification) {
        setStatus({ type: 'error', message: 'Failed to synchronize: ' + error.message });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBenchmarkAndTenantData(false);
  }, [tenantId, isSuperAdmin, dateRange]);

  const handleRefreshLive = () => {
    fetchBenchmarkAndTenantData(true);
  };

  if (loading) {
    return (
      <div className="h-96 rounded-3xl animate-pulse flex flex-col items-center justify-center space-y-4">
        <RefreshCw className="animate-spin text-indigo-600" size={32} />
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Assembling live peer indexes...</p>
      </div>
    );
  }

  // Derive active business types and metrics based strictly on live firestore records
  const businessType = tenantData?.businessType || 'General';
  const myTotalTips = myTotalTipsCalculated;

  // Real comparison data for Recharts, populated only if we have sufficient regional peer records
  const comparisonChartData = [
    {
      name: tenantData?.dbaName || 'Your Location',
      Tips: myTotalTips,
      fill: '#4f46e5'
    },
    {
      name: `${businessType} Peer Avg`,
      Tips: averageTips,
      fill: '#bae6fd'
    }
  ];

  return (
    <SubscriptionGate featureName="Benchmarks" featureId="benchmarks">
      <div className="space-y-8 animate-in fade-in duration-200">
        
        {/* Sleek, Compact Dashboard Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-[2rem] border border-gray-100 shadow-xl shadow-gray-100/20">
          <div className="space-y-1">
            <h1 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-2">
              <Sparkles size={18} className="text-indigo-600 animate-pulse" />
              TIPPING BENCHMARKS
            </h1>
            <p className="text-xs text-slate-500 font-medium">
              Dynamic local peer analysis with real-time peer averages.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as any)}
              className="px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all border border-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
            >
              <option value="last_30_days">Last 30 Days</option>
              <option value="month_to_date">Month to Date</option>
              <option value="today">Today</option>
              <option value="all_time">All Time</option>
            </select>

            <button
              onClick={handleRefreshLive}
              className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 shadow-sm border border-indigo-700 cursor-pointer"
            >
              <RefreshCw size={12} />
              Refresh
            </button>
          </div>
        </div>

        {/* Global Notifications Panel */}
        {status && (
          <div className={`p-4 rounded-2xl border flex items-start gap-3 transition-all duration-300 ${
            status.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' :
            status.type === 'error' ? 'bg-rose-500/10 border-rose-500/20 text-rose-400' :
            'bg-indigo-500/10 border-indigo-500/20 text-indigo-300'
          }`}>
            {status.type === 'success' ? <CheckCircle2 className="shrink-0 mt-0.5" size={18} /> :
             status.type === 'error' ? <AlertCircle className="shrink-0 mt-0.5" size={18} /> :
             <Info className="shrink-0 mt-0.5" size={18} />}
            <span className="text-sm font-medium">{status.message}</span>
          </div>
        )}

        {/* Bento Grid Analytics Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          
          {/* Card 1: Main Metric */}
          <div className="bg-white p-4 sm:p-6 rounded-2xl sm:rounded-[2rem] border border-gray-100 shadow-xl shadow-gray-100/30 flex flex-col justify-between relative overflow-hidden group hover:border-indigo-500/20 transition-all duration-300">
            <div className="flex justify-between items-start">
              <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-gray-400">Average Tip Yield</span>
              <div className="p-1.5 sm:p-2.5 bg-indigo-50 text-indigo-600 rounded-lg sm:rounded-xl group-hover:scale-110 transition-transform">
                <TrendingUp size={14} className="sm:w-[18px] sm:h-[18px]" />
              </div>
            </div>
            <div className="mt-2 sm:mt-4 mb-1 sm:mb-2">
              <span className="text-lg sm:text-3xl font-black text-slate-900 tracking-tight italic">
                {isInsufficient ? 'Pending' : formatCurrency(averageTips)}
              </span>
              <span className="text-[10px] sm:text-xs text-slate-400 font-bold ml-1 sm:ml-1.5">/ peer avg</span>
            </div>
            <div className="text-[10px] sm:text-xs text-slate-400 font-medium truncate">
              {isInsufficient ? 'Building peer indices' : 'Based on verified records'}
            </div>
          </div>
 
          {/* Card 2: Peer Rank */}
          <div className="bg-white p-4 sm:p-6 rounded-2xl sm:rounded-[2rem] border border-gray-100 shadow-xl shadow-gray-100/30 flex flex-col justify-between relative overflow-hidden group hover:border-indigo-500/20 transition-all duration-300">
            <div className="flex justify-between items-start">
              <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-gray-400">Peer Ranking</span>
              <div className="p-1.5 sm:p-2.5 bg-amber-50 text-amber-600 rounded-lg sm:rounded-xl group-hover:scale-110 transition-transform">
                <Award size={14} className="sm:w-[18px] sm:h-[18px]" />
              </div>
            </div>
            <div className="mt-2 sm:mt-4 mb-1 sm:mb-2">
              <span className="text-lg sm:text-3xl font-black text-slate-900 tracking-tight italic">
                {isInsufficient ? 'Pending' : `#${rank}`}
              </span>
              <span className="text-[10px] sm:text-xs text-slate-400 font-bold ml-1 sm:ml-1.5">of {totalPeers}</span>
            </div>
            <div className="text-[10px] sm:text-xs text-slate-400 font-medium truncate">
              {isInsufficient ? 'Insufficient size' : 'Compiling nationwide rank'}
            </div>
          </div>
 
          {/* Card 3: Sample Size */}
          <div className="bg-white p-4 sm:p-6 rounded-2xl sm:rounded-[2rem] border border-gray-100 shadow-xl shadow-gray-100/30 flex flex-col justify-between relative overflow-hidden group hover:border-indigo-500/20 transition-all duration-300">
            <div className="flex justify-between items-start">
              <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-gray-400">Sample Count</span>
              <div className="p-1.5 sm:p-2.5 bg-sky-50 text-sky-600 rounded-lg sm:rounded-xl group-hover:scale-110 transition-transform">
                <Users size={14} className="sm:w-[18px] sm:h-[18px]" />
              </div>
            </div>
            <div className="mt-2 sm:mt-4 mb-1 sm:mb-2">
              <span className="text-lg sm:text-3xl font-black text-slate-900 tracking-tight italic">
                {totalPeers} / {minSampleSize}
              </span>
              <span className="text-[10px] sm:text-xs text-slate-400 font-bold ml-1 sm:ml-1.5">reached</span>
            </div>
            <div className="text-[10px] sm:text-xs text-slate-400 font-medium truncate">
              Sector: <span className="text-slate-700 font-bold uppercase tracking-wider">{businessType}</span>
            </div>
          </div>
 
          {/* Card 4: Efficiency */}
          <div className="bg-white p-4 sm:p-6 rounded-2xl sm:rounded-[2rem] border border-gray-100 shadow-xl shadow-gray-100/30 flex flex-col justify-between relative overflow-hidden group hover:border-indigo-500/20 transition-all duration-300">
            <div className="flex justify-between items-start">
              <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-gray-400">Tipping Velocity</span>
              <div className="p-1.5 sm:p-2.5 bg-rose-50 text-rose-600 rounded-lg sm:rounded-xl group-hover:scale-110 transition-transform">
                <Flame size={14} className="sm:w-[18px] sm:h-[18px]" />
              </div>
            </div>
            <div className="mt-2 sm:mt-4 mb-1 sm:mb-2">
              <span className="text-lg sm:text-3xl font-black text-slate-900 tracking-tight italic">
                {isInsufficient ? 'Pending' : 'Active'}
              </span>
              <span className="text-[10px] sm:text-xs text-slate-400 font-bold ml-1 sm:ml-1.5">Rate index</span>
            </div>
            <div className="text-[10px] sm:text-xs text-slate-400 font-medium truncate">
              Real-time ledger check is active
            </div>
          </div>
 
        </div>

        {/* Unified Dashboard Tab Area Container */}
        <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-2xl p-6 md:p-8 space-y-6">
          <div className="border-b border-gray-100 pb-5">
            <h2 className="text-xl font-bold text-slate-900 tracking-tight font-sans">
              Regional Peer Performance
            </h2>
            <p className="text-xs text-gray-400 font-medium mt-1">
              Live secure comparative benchmark parameters strictly aligned to regional business registers.
            </p>
          </div>

          {/* Check if we have insufficient peer records */}
          {isInsufficient ? (
            <div className="p-8 md:p-12 flex flex-col items-center justify-center text-center space-y-6 max-w-2xl mx-auto py-12">
              <div className="w-16 h-16 bg-indigo-50 border border-indigo-100 text-indigo-600 rounded-[1.25rem] flex items-center justify-center shadow-lg shadow-indigo-100/40">
                <Users size={28} className="animate-pulse text-indigo-500" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-black uppercase text-slate-800 tracking-tight">Awaiting Live Regional Peer Data</h3>
                <p className="text-sm text-slate-500 leading-relaxed font-sans font-medium">
                  We require a minimum of <span className="text-slate-800 font-bold">{minSampleSize} active locations</span> categorized as <span className="text-slate-800 font-bold uppercase">"{businessType}"</span> in your geographical sector to construct stable, anonymous comparison indices.
                </p>
                <p className="text-xs text-gray-400 font-medium font-mono pt-1">
                  Current Directory Count: {totalPeers} / {minSampleSize} linked endpoints.
                </p>
              </div>

              <div className="w-full bg-slate-50 p-6 rounded-3xl border border-gray-200/55 text-left space-y-3 max-w-md">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Benchmark Compilation Roadmap</p>
                
                <div className="flex items-center gap-3 text-xs font-semibold text-emerald-600">
                  <CheckCircle2 size={16} className="shrink-0" />
                  <span>Local store verified: {tenantData?.dbaName || 'Connected Store'}</span>
                </div>
                
                <div className="flex items-center gap-3 text-xs font-semibold text-emerald-600">
                  <CheckCircle2 size={16} className="shrink-0" />
                  <span>Sector mapping validated: {businessType}</span>
                </div>

                <div className="flex items-center gap-3 text-xs font-semibold text-indigo-500">
                  <RefreshCw size={16} className="animate-spin shrink-0" />
                  <span>Accumulating active local peers ({totalPeers} of {minSampleSize})</span>
                </div>
                
                <div className="flex items-center gap-3 text-xs font-semibold text-gray-400">
                  <div className="w-4 h-4 rounded-full border border-gray-300 flex items-center justify-center text-[10px] shrink-0 font-bold">4</div>
                  <span>Generate real-time comparison bell curves</span>
                </div>
              </div>
            </div>
          ) : (
            // Render Live Real Comparison graph if sample requirements met!
            <div className="h-[22rem] w-full pt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparisonChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} fontWeight={600} />
                  <YAxis stroke="#94a3b8" fontSize={11} fontWeight={600} tickFormatter={(v) => `$${v}`} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '1rem', border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)' }}
                    formatter={(value: any) => [`$${value.toFixed(2)}`, 'Allocated Tips']}
                  />
                  <Legend verticalAlign="top" height={36} />
                  <Bar dataKey="Tips" name="Allocated Tips Total ($)" radius={[8, 8, 0, 0]} barSize={40}>
                    {comparisonChartData.map((entry, index) => (
                      <rect key={`rect-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

      </div>
    </SubscriptionGate>
  );
};
